// The action step as a focused dialog over the board: options grouped by category,
// reopened from the decision panel so the board stays reachable.
import { AnimatePresence, motion } from "framer-motion";

import * as uf from "../../../../common/unfathomable";
import { DragPanel } from "./DragPanel";
import { MiniMap, spaceTargets } from "./MiniMap";
import { Rich } from "./RichText";
import css from "./Unfathomable.module.css";

interface Category { key: string; icon: string; title: string; groups: string[] }

const kCategories: Category[] = [
  { key: "move", icon: "🧭", title: "移动", groups: ["移动"] },
  { key: "fight", icon: "⚔", title: "战斗与救援", groups: ["攻击", "救援"] },
  { key: "space", icon: "⚙", title: "所在空间", groups: ["空间行动"] },
  { key: "cards", icon: "🂠", title: "技能卡", groups: ["技能卡"] },
  { key: "gear", icon: "🎒", title: "物品与面前的卡", groups: ["物品", "面前的卡", "交易"] },
  { key: "role", icon: "★", title: "角色与头衔", groups: ["角色能力", "头衔"] },
  { key: "dark", icon: "🩸", title: "叛徒", groups: ["公开", "背叛"] },
];

/** Categories shown under the map, in the left column. */
const kLeft = ["fight", "space"];

function categoryOf(group: string | undefined): string {
  return kCategories.find(c => c.groups.includes(group ?? ""))?.key ?? "other";
}

/** "打出 X（影响 3）：effect text" → name and effect on separate lines. */
function OptionLabel({ label }: { label: string }) {
  const i = label.indexOf("：");
  if (i < 0) return <Rich text={label} />;
  return (
    <>
      <div className={css.actOptName}><Rich text={label.slice(0, i)} /></div>
      <div className={css.actOptText}><Rich text={label.slice(i + 1)} /></div>
    </>
  );
}

export function ActionDialog(props: {
  view: uf.GameView;
  open: boolean;
  setOpen: (open: boolean) => void;
  onAnswer: (choice: string[]) => void;
  artOk: boolean;
}) {
  const { view, open, setOpen, onAnswer, artOk } = props;
  const d = view.decision;
  const active = !!d && d.mine && d.kind === "action" && !view.gameOver;
  if (!active) return null;
  const end = d.options.find(o => o.id === "pass");
  const buckets = new Map<string, uf.Option[]>();
  for (const o of d.options) {
    if (o.id === "pass") continue;
    const k = categoryOf(o.group);
    buckets.set(k, [...(buckets.get(k) ?? []), o]);
  }
  const moveOpts = buckets.get("move") ?? [];
  // Attack and rescue are always listed; when unavailable they show why (greyed out).
  const me = view.you !== null ? view.players[view.you] : null;
  const fight = buckets.get("fight") ?? [];
  const unavailable: { label: string; why: string }[] = [];
  if (me && !fight.some(o => o.id.startsWith("attack:"))) unavailable.push({ label: "⚔ 攻击", why: "本空间没有可攻击的目标" });
  if (me && !me.revealed && !fight.some(o => o.id === "rescue")) {
    unavailable.push({ label: "🛟 救援乘客", why: uf.spaceKind(me.space) === "deck" ? "本空间没有乘客" : "只能在有乘客的甲板上救援" });
  }
  const cats = [...kCategories, { key: "other", icon: "✦", title: "其他", groups: [] }]
    .filter(c => buckets.has(c.key) || (c.key === "fight" && unavailable.length > 0));

  const renderCat = (c: Category) => (
    <div key={c.key} className={css.actCat}>
      <div className={css.actCatTitle}>{c.icon} {c.title}</div>
      <div className={css.actList}>
        {(buckets.get(c.key) ?? []).map(o => (
          <button key={o.id} className={`${css.btn} ${css.actOpt} ${c.key === "dark" ? css.btnDanger : ""}`} onClick={() => onAnswer([o.id])} title={o.detail}>
            <OptionLabel label={o.label} />
          </button>
        ))}
        {c.key === "fight" && unavailable.map(u => (
          <button key={u.label} className={`${css.btn} ${css.actOpt}`} disabled title={u.why}>
            <div className={css.actOptName}>{u.label}</div>
            <div className={css.actOptText}>{u.why}</div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <AnimatePresence mode="wait">
      {open ? (
        <motion.div key="dlg" className={css.actBackdrop} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <DragPanel className={`${css.actDialog} ${css.frame}`}>
            <div className={css.actHead}>
              <div>
                <div className={css.actTitle}>你的行动</div>
                <div className={css.actSub}><PromptLine text={d.prompt} /></div>
              </div>
              <div className={css.actLeft} title="本回合剩余的行动次数">
                <span>剩余行动</span>
                <b>{view.actionsLeft}</b>
                <span className={css.actPips}>{Array.from({ length: Math.max(view.actionsLeft, 0) }, (_, i) => <i key={i} />)}</span>
              </div>
              <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={() => setOpen(false)} title="收起面板，查看地图">收起 ▾</button>
            </div>
            {/* Left: where you are and where you can go (map, fight & rescue, the space). Right: cards and abilities. */}
            <div className={css.actLayout}>
              <div className={css.actSideCol}>
                {moveOpts.length > 0 && (
                  <div className={css.actCat}>
                    <div className={css.actCatTitle}>🧭 移动<span className={css.actCatHint}>点击地图上发光的空间</span></div>
                    <MiniMap view={view} artOk={artOk} targets={spaceTargets(moveOpts)} onPick={id => onAnswer([id])} />
                  </div>
                )}
                {cats.filter(c => kLeft.includes(c.key)).map(renderCat)}
              </div>
              <div className={css.actSideCol}>
                {cats.filter(c => c.key !== "move" && !kLeft.includes(c.key)).map(renderCat)}
              </div>
            </div>
            {end && (
              <div className={css.actFoot}>
                <button className={`${css.btn} ${css.btnGhost}`} onClick={() => onAnswer([end.id])}>⏹ {end.label}</button>
              </div>
            )}
          </DragPanel>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function PromptLine({ text }: { text: string }) {
  return <Rich text={text.split("｜")[0]} />;
}
